import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface ExportJob {
  id: string
  customer_name: string
  type: string
  format: string
  status: string
  tables: string[]
  file_size_mb: number | null
  records_exported: number | null
  download_url: string | null
  progress_percentage?: number
  created_at: string
  completed_at: string | null
}

interface ImportJob {
  id: string
  customer_name: string
  type: string
  format: string
  status: string
  target_table: string
  file_name: string
  file_size_mb: number
  records_total: number | null
  records_imported: number | null
  records_failed: number | null
  validation_progress?: number
  error_message?: string
  created_at: string
}

interface Template {
  id: string
  name: string
  type: string
  format: string
  description: string
  download_url: string
}

export default function DataExportPage() {
  const [exportJobs, setExportJobs] = useState<ExportJob[]>([])
  const [importJobs, setImportJobs] = useState<ImportJob[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'exports' | 'imports'>('exports')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [expRes, impRes, tplRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/data-export/exports'),
      fetch(API_BASE + '/data-export/imports'),
      fetch(API_BASE + '/data-export/templates'),
      fetch(API_BASE + '/data-export/statistics')
    ])
    if (expRes.ok) setExportJobs((await expRes.json()).export_jobs || [])
    if (impRes.ok) setImportJobs((await impRes.json()).import_jobs || [])
    if (tplRes.ok) setTemplates((await tplRes.json()).templates || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'processing': case 'validating': return 'bg-blue-100 text-blue-800'
      case 'queued': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatSize = (mb: number | null) => {
    if (mb === null) return '-'
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`
    return `${mb.toFixed(1)} MB`
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Data Export/Import Manager</h1>
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Export</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ New Import</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Export Jobs</div>
              <div className="text-2xl font-bold">{statistics.total_export_jobs}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Exports Done</div>
              <div className="text-2xl font-bold text-green-600">{statistics.completed_exports}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Import Jobs</div>
              <div className="text-2xl font-bold">{statistics.total_import_jobs}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Imports Done</div>
              <div className="text-2xl font-bold text-green-600">{statistics.completed_imports}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Records Exported</div>
              <div className="text-2xl font-bold text-blue-600">{(statistics.total_records_exported / 1000000).toFixed(1)}M</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Templates</div>
              <div className="text-2xl font-bold">{statistics.templates_available}</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setActiveTab('exports')} className={`px-4 py-2 rounded-lg ${activeTab === 'exports' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Exports</button>
          <button onClick={() => setActiveTab('imports')} className={`px-4 py-2 rounded-lg ${activeTab === 'imports' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Imports</button>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-semibold">{activeTab === 'exports' ? 'Export Jobs' : 'Import Jobs'}</h2>
            </div>
            {activeTab === 'exports' ? (
              <div className="divide-y">
                {exportJobs.map(job => (
                  <div key={job.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold">{job.type.replace(/_/g, ' ')}</div>
                        <div className="text-sm text-gray-500">{job.customer_name}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(job.status)}`}>{job.status}</span>
                    </div>
                    <div className="flex gap-4 text-sm text-gray-500 mb-2">
                      <span>Format: {job.format.toUpperCase()}</span>
                      <span>Tables: {job.tables.join(', ')}</span>
                    </div>
                    {job.status === 'processing' && job.progress_percentage !== undefined && (
                      <div className="mb-2">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full" style={{ width: `${job.progress_percentage}%` }}></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{job.progress_percentage}% complete</div>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <div className="text-sm">
                        {job.records_exported !== null && <span className="mr-4">{job.records_exported.toLocaleString()} records</span>}
                        {job.file_size_mb !== null && <span>{formatSize(job.file_size_mb)}</span>}
                      </div>
                      {job.download_url && (
                        <button className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200">Download</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y">
                {importJobs.map(job => (
                  <div key={job.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold">{job.file_name}</div>
                        <div className="text-sm text-gray-500">{job.customer_name}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(job.status)}`}>{job.status}</span>
                    </div>
                    <div className="flex gap-4 text-sm text-gray-500 mb-2">
                      <span>Target: {job.target_table}</span>
                      <span>Format: {job.format.toUpperCase()}</span>
                      <span>Size: {formatSize(job.file_size_mb)}</span>
                    </div>
                    {job.status === 'validating' && job.validation_progress !== undefined && (
                      <div className="mb-2">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 rounded-full" style={{ width: `${job.validation_progress}%` }}></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Validating: {job.validation_progress}%</div>
                      </div>
                    )}
                    {job.error_message && (
                      <div className="text-sm text-red-600 mb-2">{job.error_message}</div>
                    )}
                    <div className="text-sm">
                      {job.records_imported !== null && (
                        <span className="mr-4 text-green-600">{job.records_imported.toLocaleString()} imported</span>
                      )}
                      {job.records_failed !== null && job.records_failed > 0 && (
                        <span className="text-red-600">{job.records_failed} failed</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Templates</h3>
            <div className="space-y-3">
              {templates.map(tpl => (
                <div key={tpl.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{tpl.name}</span>
                    <span className="px-2 py-0.5 bg-gray-200 rounded text-xs uppercase">{tpl.format}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{tpl.description}</p>
                  <button className="text-sm text-blue-600 hover:text-blue-800">Download Template</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
